import React, { useMemo, useRef, useState } from 'react'
import {
  ComposePanel,
  type ComposePanelHandle,
  type SendPayload,
} from '../NewCompanyPage/Modals/EmailsModal/ComposePanel'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  requiredFirstNameValidator,
  requiredLastNameValidator,
  requiredEmailValidator,
  phoneNumberZodValidor,
} from '../../utils/zodValidators'
import { CompanyAutocomplete } from '../CompanyAutocomplete'
import {
  ArrowRight,
  Building2,
  Contact,
  Handshake,
  Play,
  X,
} from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { EmailsModal } from '../NewCompanyPage/Modals/EmailsModal/EmailsModal'
import { getEmailMessageById } from '../../services/emailIntegrationApi'
import { cn } from '../UI/cn'
import { Tooltip } from '../Tooltip/tooltip'
import { Tabs } from '../FbUI/Tabs'
import { RightPanel } from './RightPanel'
import { RecommendationActivityTab } from './RecommendationActivityTab'
import Checkbox from '../Checkbox/checkbox'
import { NoteManager } from '../Notes/NoteManager'
import { ProductSelector } from '../UI/ProductSelector'
import { StagePill } from '../FbUI/StagePill'
import { TextPill } from '../FbUI/TextPill'
import { StageColorProvider } from '../SalesStage/StageColorContext'
import {
  DEFAULT_STAGE_COLOR,
  getPipelineColorForStage,
  sortStagesPipelineOrder,
  isTerminalStage,
} from '../SalesStage/stageColors'
import { colors } from '../../styles/tokens'
import { useMountEffect } from '../../hooks/useMountEffect'
import { usePreferences } from '../../context/preferences/PreferencesContext'
import { PhoneInput } from '../FormUtils/PhoneInput'
import ReactSelectWrapper from '../FormUtils/ReactSelectWrapper'
import { DatePicker } from '../FormUtils/DatePicker'
import {
  REMINDER_TITLE_MAX_LENGTH,
  reminderApplyEditSchema,
  reminderRunAtDateSchema,
} from '../Reminders/reminderValidation'
import useApiService from '../../services/api'
import { useAuth } from '../../context/authentication/useAuth'
import { notifyInfo } from '../../services/toast'
import type { SingleValue } from 'react-select'
import type {
  EnrichedAction,
  PreparedAction,
  AddNoteAction,
  ActionClickStatus,
  ActionClickUpdateStatus,
  ResolutionStatus,
} from '../../services/recommendations_contracts'
import { SIGNAL_CONFIG } from './recommendationConfig'
import {
  URGENCY_CIRCLE_STYLES,
  URGENCY_ICONS,
  URGENCY_TOOLTIPS,
} from './constants'
import { resolveAgentIcon } from './agentIcons'
import type { RecommendationAgent } from '../../models/companies'

interface EnrichedActionCardProps {
  action: EnrichedAction
  /** Optional callback when the card is clicked (for tracking) */
  onCardClick?: () => void
  /** Optional callback when user clicks Dismiss */
  onStatusClick?: (status: ActionClickUpdateStatus) => void
  /** Current action status (for visual treatment) */
  actionStatus?: ActionClickStatus | null
  /** Whether the action was auto-completed by the system */
  actionAutoCompleted?: boolean
  /** Fired after the atomic apply succeeds server-side. The backend has
   *  already marked the rec resolved + recorded the click; the parent
   *  should refetch summary data. */
  onApplied?: () => void
  /** Fired when the user clicks Apply with zero prepared actions
   *  selected — there's nothing to send to the backend, so just mark
   *  the rec done client-side. */
  onNoOpResolve?: () => void
  /** When true, Apply/Dismiss buttons are disabled (viewing another user's recs) */
  readOnly?: boolean
  /** When true, actions are disabled while LLM re-evaluates */
  isRefreshing?: boolean
  /** Fired when the user clicks the card's Apply button — parent
   *  opens the apply panel for this action. */
  onApplyClick?: (action: EnrichedAction) => void
  /**
   * User-facing agents (from company-details). Used to look up the chip
   * label for this card via the agent that owns the rec's signal_type —
   * single source of truth shared with the section header.
   */
  recommendationAgents?: RecommendationAgent[]
  /**
   * Whether to dim the card (reduce opacity) when its action status is
   * done or dismissed. True for the active list — visually marks
   * already-handled rows. False for the recently-completed side panel
   * where every card is resolved by definition and dimming is just
   * noise that hurts readability.
   */
  dimWhenResolved?: boolean
}

const DISABLED_TOOLTIP: Record<
  'stale' | 'finalized' | 'readonly' | 'refreshing',
  { title: string; content: string }
> = {
  stale: {
    title: 'Outdated',
    content:
      'The deal stage has already changed — this action is no longer applicable.',
  },
  finalized: {
    title: 'Already actioned',
    content:
      'This recommendation has already been marked as done or dismissed.',
  },
  readonly: {
    title: 'Read-only',
    content: 'You can only apply actions on your own recommendations.',
  },
  refreshing: {
    title: 'Re-evaluating',
    content:
      'This recommendation is being re-evaluated based on recent activity.',
  },
}

// ---------------------------------------------------------------------------
// Multi-Action Confirmation Dialog
// ---------------------------------------------------------------------------

interface NoteFormValues {
  note: string
  attachments: File[]
}

// Zod schema for the rec card's inline update_contact form. Mirrors
// create_contact: each editable field is optional (the backend already
// rejects payloads where every field is empty), phone uses the shared
// E.164 validator, and the form is only used when the LLM suggested at
// least one target field (the rec card hides fields where target is
// null entirely).
const updateContactSchema = z.object({
  title: z.string().optional().default(''),
  phone_number: z
    .union([phoneNumberZodValidor, z.literal(''), z.null()])
    .optional(),
})

type UpdateContactFormValues = z.infer<typeof updateContactSchema>

// Zod schema for the rec card's inline create_contact form. Reuses the
// shared field validators from utils/zodValidators so error messages and
// strictness stay aligned with future tightening at that layer.
// ContactModal hosts its own schema with a wider field surface — this is
// intentionally just the subset the rec card collects.
const createContactSchema = z.object({
  first_name: requiredFirstNameValidator,
  last_name: requiredLastNameValidator,
  email: requiredEmailValidator,
  // CompanyAutocomplete writes either an existing company (id > 0,
  // sourced from the API) or a synthesized stub from CreatableSelect's
  // ``Create "X"`` option (id === -1, name = the user-typed string).
  // Both are valid form states; the apply payload builder branches on
  // id so the backend either reuses the existing ContactCompany or
  // creates a new one from ``new_company_name``.
  contact_company: z
    .object({
      id: z.number(),
      name: z.string().optional(),
      domain: z.string().nullable().optional(),
    })
    .nullable()
    .refine(
      (v) => {
        if (v == null) return false
        if (v.id > 0) return true
        if (v.id === -1 && (v.name?.trim().length ?? 0) > 0) return true
        return false
      },
      { message: 'Pick or create a company' }
    ),
  title: z.string().optional().default(''),
  phone_number: z
    .union([phoneNumberZodValidor, z.literal(''), z.null()])
    .optional(),
})

type CreateContactFormValues = z.infer<typeof createContactSchema>

/** Convert plain-text paragraphs (separated by blank lines) into Quill-compatible HTML. */
function plainTextToHtml(text: string): string {
  const escape = (s: string) =>
    s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  return text
    .split('\n\n')
    .map((p) => `<p>${escape(p).replace(/\n/g, '<br>')}</p>`)
    .join('')
}

/** Entity summary line showing company/deal/contact context.
 *
 * Uses the same icon vocabulary as the deal-detail page sidebar
 * (Building2 for company, Contact for contact) plus Handshake for the
 * deal name. Each entity reads "icon + text"; the icons replace what
 * used to be vertical-pipe separators.
 *
 * Lives in the card footer alongside Apply/Dismiss; the deal_name and
 * company_name are clickable links so this row replaces the older
 * "View deal" / "View company" button. ``onCardClick`` fires before
 * navigation so impression tracking on the parent still records the
 * click — same hook the old button used. */
const EntitySummary: React.FC<{
  action: EnrichedAction
  onCardClick?: () => void
}> = ({ action, onCardClick }) => {
  // Show deal name when the card links to a deal (linkTarget 'deal' or 'auto'
  // when the action has a deal_id). Company-linked cards skip the deal name
  // since they're about the company as a whole.
  const linkTarget = SIGNAL_CONFIG[action.signal_type]?.linkTarget
  const showDealName =
    linkTarget !== 'company' && !!action.deal_name && !!action.deal_id
  // Recs without a bound ContactCompany (today: contact_suggestion ``create``
  // flavor — the picker assigns one at apply time) have no meaningful chip
  // to show. Capture the link in a const so TS narrows nullability at the
  // JSX use site; otherwise the boolean derived from ``action.company_link``
  // doesn't propagate the narrowing through to ``<Link to={...}>``.
  const companyLink = action.company_link
  const showCompany = !!action.contact_company_id && companyLink != null
  const handleLinkClick = (e: React.MouseEvent) => {
    // Stop the parent card's onClick (which toggles the long-text
    // expand state) so navigating doesn't also flip that toggle.
    e.stopPropagation()
    if (onCardClick) onCardClick()
  }
  return (
    <div className="flex items-center flex-wrap gap-x-3 gap-y-1 text-base text-fg-secondary min-w-0">
      {showDealName && (
        <span className="inline-flex items-center gap-1">
          <Handshake size={16} className="text-fg-muted flex-shrink-0" />
          <Link
            to={`/deals/${action.deal_id}/`}
            onClick={handleLinkClick}
            className="font-medium text-fg-secondary underline decoration-line-subtle underline-offset-2 hover:decoration-fg-secondary"
          >
            {action.deal_name}
          </Link>
        </span>
      )}
      {showCompany && companyLink != null && (
        <span className="inline-flex items-center gap-1">
          <Building2 size={16} className="text-fg-muted flex-shrink-0" />
          <Link
            to={companyLink}
            onClick={handleLinkClick}
            className="text-fb-dark-green font-medium underline decoration-line-subtle underline-offset-2 hover:decoration-fb-dark-green"
          >
            {action.company_name}
          </Link>
        </span>
      )}
      {action.contact_name && (
        <span className="inline-flex items-center gap-1">
          <Contact size={16} className="text-fg-muted flex-shrink-0" />
          <span>{action.contact_name}</span>
        </span>
      )}
    </div>
  )
}

interface ApplyRecommendationPanelProps {
  /** Always rendered when present — caller controls show/hide via
   *  conditional render. ``onClose`` fires when the user dismisses
   *  via the panel's close button or Cancel. */
  onClose: () => void
  preparedActions: PreparedAction[]
  action: EnrichedAction
  /** Hand the prepared FormData up to the parent so it can fire the
   *  apply request in the background while the panel closes. The
   *  panel does NOT wait for the response — the parent disables the
   *  card for the ~1s flight and shows the success/failure toast. */
  onApply: (formData: FormData, count: number) => void
  /** Fired when the user confirms with zero actions selected. */
  onNoOpResolve: () => void
  mutuallyExclusiveActions: string[][]
  /**
   * Whether the rec owner has at least one connected email integration.
   * When false, any ``send_email`` prepared action is forced unchecked,
   * its checkbox is disabled, and the body slot is replaced with a CTA
   * to connect an integration. Avoids the otherwise-silent 409 the
   * backend would return after the user filled in everything else.
   */
  hasEmailIntegration: boolean
  /** Agents catalog used by the rec-context block to look up the
   *  signal-type icon + label. Same one EnrichedActionCard uses. */
  recommendationAgents?: RecommendationAgent[]
}

/**
 * Right-panel apply flow. Shows the rec's context (priority badge,
 * type badge, entity chips, short title, long summary) at the top,
 * then the editable list of prepared actions. The footer has the
 * Cancel + Apply (N) buttons pinned in place; the body scrolls
 * underneath.
 *
 * Rendered by RecommendationsCard inside the homepage right-panel
 * host when ``activePanel.kind === 'apply-rec'``. Replaces the older
 * MultiActionConfirmDialog modal.
 */
export const ApplyRecommendationPanel: React.FC<
  ApplyRecommendationPanelProps
> = ({
  onClose,
  preparedActions,
  action,
  onApply,
  onNoOpResolve,
  mutuallyExclusiveActions,
  hasEmailIntegration,
  recommendationAgents = [],
}) => {
  const [enabled, setEnabled] = useState<boolean[]>(() =>
    preparedActions.map(
      (pa) =>
        !pa.stale &&
        pa.type !== 'delete_reminder' &&
        // Force send_email unchecked when there's no integration to send through.
        (pa.type !== 'send_email' || hasEmailIntegration)
    )
  )

  // Imperative handle so the modal's global Apply button can trigger the
  // embedded ComposePanel's submit. The compose form lives in its own
  // useForm instance (zod-validated) inside ComposePanel.
  const composeRef = useRef<ComposePanelHandle>(null)
  // Captures whatever ComposePanel produces on submit. Synchronously written
  // in the onSend callback so handleConfirm can read it immediately after
  // calling composeRef.current?.submit().
  const composePayloadRef = useRef<SendPayload | null>(null)

  const stageAction = preparedActions.find((pa) => pa.type === 'update_stage')
  const [selectedStageId, setSelectedStageId] = useState<number | null>(
    stageAction?.type === 'update_stage' ? stageAction.target_stage_id : null
  )

  const closeDateAction = preparedActions.find(
    (pa) => pa.type === 'update_close_date'
  )
  const [selectedCloseDate, setSelectedCloseDate] = useState<
    string | undefined
  >(
    closeDateAction?.type === 'update_close_date'
      ? closeDateAction.target_close_date
      : undefined
  )

  const createReminderAction = preparedActions.find(
    (pa) => pa.type === 'create_reminder'
  )
  const [reminderTitle, setReminderTitle] = useState(
    createReminderAction?.type === 'create_reminder'
      ? createReminderAction.reminder_title
      : ''
  )
  const [selectedReminderDate, setSelectedReminderDate] = useState<
    string | undefined
  >(
    createReminderAction?.type === 'create_reminder'
      ? createReminderAction.reminder_date
      : undefined
  )

  const rescheduleAction = preparedActions.find(
    (pa) => pa.type === 'reschedule_reminder'
  )
  const [selectedRescheduleDate, setSelectedRescheduleDate] = useState<
    string | undefined
  >(
    rescheduleAction?.type === 'reschedule_reminder'
      ? rescheduleAction.target_date
      : undefined
  )

  // create_contact — every field is user-editable now (the LLM extraction
  // is a starting point, not a contract). All editable values flow through
  // the ``createContactForm`` below so zod validation catches bad data
  // before submit.
  const createContactAction = preparedActions.find(
    (pa) => pa.type === 'create_contact'
  )

  // update_contact — every suggested field is shown as an editable
  // input prefilled with the LLM's target value. Fields the LLM
  // didn't suggest (target_* === null) aren't rendered at all,
  // replacing the older diff-with-checkboxes UX. The user can edit
  // before applying, or clear a field to skip applying it. Backend
  // rejects payloads where every target_* is empty (400), so the
  // payload builder also bails out client-side to avoid the
  // round-trip in the no-op case.
  const updateContactAction = preparedActions.find(
    (pa) => pa.type === 'update_contact'
  )

  // create_deal — product picker is required; deal name / close date /
  // volume / revenue are LLM-suggested + user-editable.
  //
  // Invariant: at most one ``create_deal`` prepared action per rec/panel.
  // The detector dedups per-(contact_company) and emits one signal per
  // company → one rec → one prepared action. If this ever changes (the
  // synthesis builder starts emitting multiple ``create_deal`` actions
  // for one rec), the single-state pattern below silently broadcasts the
  // first row's pre-fill to every row at apply time — switch to
  // per-action keyed state (e.g. ``Map<index, FormValues>``) before
  // shipping that.
  const createDealAction = preparedActions.find(
    (pa) => pa.type === 'create_deal'
  )
  const [createDealProductId, setCreateDealProductId] = useState<number | null>(
    null
  )
  const [createDealName, setCreateDealName] = useState(
    createDealAction?.type === 'create_deal'
      ? (createDealAction.target_deal_name ?? '')
      : ''
  )
  const [createDealCloseDate, setCreateDealCloseDate] = useState<
    string | undefined
  >(
    createDealAction?.type === 'create_deal'
      ? (createDealAction.target_close_date ?? undefined)
      : undefined
  )
  const [createDealVolume, setCreateDealVolume] = useState<string>(
    createDealAction?.type === 'create_deal' &&
      createDealAction.target_monthly_volume != null
      ? String(createDealAction.target_monthly_volume)
      : ''
  )
  const [createDealRevenue, setCreateDealRevenue] = useState<string>(
    createDealAction?.type === 'create_deal' &&
      createDealAction.target_annual_revenue != null
      ? String(createDealAction.target_annual_revenue)
      : ''
  )

  const api = useApiService()
  const { user } = useAuth()
  const { data: stages } = api.useGetSalesStages()
  const { isSettingEnabled } = usePreferences()
  const customOrderEnabled = isSettingEnabled('sales_stage_custom_order')

  const pipelineStageOptions = useMemo(() => {
    if (!stages) return []
    const sorted = sortStagesPipelineOrder(stages, customOrderEnabled)
    const regular = sorted.filter((s) => !isTerminalStage(s))
    const terminal = sorted.filter((s) => isTerminalStage(s))
    return [...regular, ...terminal].map((s) => {
      const pipelineColor = getPipelineColorForStage(
        s.id,
        stages,
        customOrderEnabled
      )
      return {
        label: s.stage,
        value: s.id,
        bg: pipelineColor?.bg ?? DEFAULT_STAGE_COLOR,
        text: pipelineColor?.text ?? colors['fg'],
      }
    })
  }, [stages, customOrderEnabled])

  const selectedStageOption = useMemo(
    () => pipelineStageOptions.find((o) => o.value === selectedStageId) ?? null,
    [pipelineStageOptions, selectedStageId]
  )

  const noteAction = preparedActions.find(
    (pa): pa is AddNoteAction => pa.type === 'add_note'
  )

  type PanelTab = 'quick-actions' | 'activity'
  const [activeTab, setActiveTab] = useState<PanelTab>('quick-actions')

  // "See thread" affordance for send_email actions whose trigger is an
  // existing inbound. Holds the EmailMessage Django PK to fetch +
  // open in the same EmailsModal the activity timeline uses. Null when
  // the modal is closed.
  const [threadEmailId, setThreadEmailId] = useState<number | null>(null)
  const { data: threadEmail } = useQuery({
    queryKey: ['recommendations-trigger-email', threadEmailId],
    queryFn: () => getEmailMessageById(threadEmailId!),
    enabled: threadEmailId != null,
    staleTime: 5 * 60 * 1000,
  })

  const { control, getValues } = useForm<NoteFormValues>({
    defaultValues: {
      note: noteAction ? plainTextToHtml(noteAction.draft_content) : '',
      attachments: [],
    },
  })

  // Dedicated form for the create_contact panel. Zod resolver enforces
  // required name + valid email + picked company before Apply enables.
  // The watched values feed the apply payload at submit time.
  const createContactDefaults = useMemo<CreateContactFormValues>(
    () => ({
      first_name:
        createContactAction?.type === 'create_contact'
          ? createContactAction.first_name
          : '',
      last_name:
        createContactAction?.type === 'create_contact'
          ? createContactAction.last_name
          : '',
      email:
        createContactAction?.type === 'create_contact'
          ? createContactAction.email
          : '',
      contact_company: null,
      title:
        createContactAction?.type === 'create_contact'
          ? (createContactAction.title ?? '')
          : '',
      phone_number:
        createContactAction?.type === 'create_contact'
          ? (createContactAction.phone_number ?? '')
          : '',
    }),
    [createContactAction]
  )
  const createContactForm = useForm<CreateContactFormValues>({
    defaultValues: createContactDefaults,
    resolver: zodResolver(createContactSchema),
    // ``all`` mode validates on every change *and* every blur. Combined
    // with the mount-time trigger() below, this means errors are
    // visible from the moment the panel opens — not gated on the user
    // having interacted with each field.
    mode: 'all',
  })
  const {
    control: createContactControl,
    register: registerCreateContact,
    formState: { errors: createContactErrors, isValid: createContactIsValid },
    getValues: getCreateContactValues,
    trigger: triggerCreateContact,
  } = createContactForm
  const selectedContactCompany = createContactForm.watch('contact_company')

  // Validate every field on mount so required-field errors (most
  // commonly the empty Company picker) render immediately. Without
  // this, the user only learns the form is incomplete by hitting a
  // disabled Apply button with no inline cue.
  useMountEffect(() => {
    if (createContactAction) {
      void triggerCreateContact()
    }
  })

  // update_contact form: prefilled with the LLM's target_* values for
  // whichever fields it suggested. Fields the LLM didn't suggest
  // remain at their schema default ('' / null) and are simply not
  // rendered, so the user only ever sees inputs that actually map to
  // an in-flight suggestion.
  const updateContactDefaults = useMemo<UpdateContactFormValues>(
    () => ({
      title:
        updateContactAction?.type === 'update_contact' &&
        updateContactAction.target_title != null
          ? updateContactAction.target_title
          : '',
      phone_number:
        updateContactAction?.type === 'update_contact' &&
        updateContactAction.target_phone_number != null
          ? updateContactAction.target_phone_number
          : '',
    }),
    [updateContactAction]
  )
  const updateContactForm = useForm<UpdateContactFormValues>({
    defaultValues: updateContactDefaults,
    resolver: zodResolver(updateContactSchema),
    mode: 'onTouched',
  })
  const {
    control: updateContactControl,
    register: registerUpdateContact,
    formState: { errors: updateContactErrors },
    getValues: getUpdateContactValues,
  } = updateContactForm

  const toggleAction = (index: number) => {
    // Block toggling send_email on when there's no integration — the user
    // would just hit a backend 409 after filling everything else in.
    const pa = preparedActions[index]
    if (pa.type === 'send_email' && !hasEmailIntegration) return

    setEnabled((prev) => {
      const next = [...prev]
      next[index] = !next[index]

      // Enforce mutual exclusion from backend config
      if (next[index]) {
        const toggledType = preparedActions[index].type
        for (const group of mutuallyExclusiveActions) {
          if (group.includes(toggledType)) {
            preparedActions.forEach((pa, i) => {
              if (i !== index && group.includes(pa.type)) {
                next[i] = false
              }
            })
          }
        }
      }

      return next
    })
  }

  const enabledCount = enabled.filter(Boolean).length

  // Validation for the inline reminder editors. Reuses the same zod
  // schema the standalone ``ReminderModal`` uses (extracted to
  // ``reminderValidation.ts``) so the inline form and the modal stay
  // aligned with the backend serializer rules. Errors are surfaced
  // below the offending field; Apply is disabled until they're cleared.
  const createReminderError = useMemo(() => {
    const idx = preparedActions.findIndex((pa) => pa.type === 'create_reminder')
    if (idx < 0 || !enabled[idx]) return null
    const result = reminderApplyEditSchema.safeParse({
      title: reminderTitle ?? '',
      runAt: selectedReminderDate ?? '',
    })
    if (result.success) return null
    // Surface the first error message — multiple field errors collapse
    // into one inline string for this compact editor.
    return result.error.issues[0]?.message ?? 'Invalid reminder'
  }, [preparedActions, enabled, reminderTitle, selectedReminderDate])

  const rescheduleReminderError = useMemo(() => {
    const idx = preparedActions.findIndex(
      (pa) => pa.type === 'reschedule_reminder'
    )
    if (idx < 0 || !enabled[idx]) return null
    // Reschedule only mutates the run date — the title isn't being
    // edited here, so we validate the date field on its own rather than
    // running the combined create-editor schema with a sentinel title.
    const result = reminderRunAtDateSchema.safeParse(
      selectedRescheduleDate ?? ''
    )
    if (result.success) return null
    return result.error.issues[0]?.message ?? 'Invalid reminder date'
  }, [preparedActions, enabled, selectedRescheduleDate])

  const hasReminderError = Boolean(
    createReminderError || rescheduleReminderError
  )

  const createDealRowChecked = preparedActions.some(
    (pa, i) => pa.type === 'create_deal' && enabled[i]
  )
  const createDealMissingProduct = createDealRowChecked && !createDealProductId
  // create_contact must satisfy zod validation (required name + valid
  // email + picked company) before apply. Gates the button and the click
  // handler. Only fires when the user has the action toggled on;
  // toggling it off releases the gate.
  const createContactIsEnabled = preparedActions.some(
    (pa, i) => enabled[i] && pa.type === 'create_contact'
  )
  const createContactInvalid = createContactIsEnabled && !createContactIsValid

  const handleConfirm = async (e: React.MouseEvent) => {
    e.stopPropagation()

    // Block submit if any enabled reminder editor has a validation
    // error. ``hasReminderError`` only fires for actions the user
    // currently has *enabled*, so disabled actions don't gate Apply.
    if (hasReminderError) return
    if (createDealMissingProduct) return
    if (createContactInvalid) return

    // No prepared actions selected → backend doesn't need to know;
    // just resolve client-side.
    const enabledIndices = preparedActions
      .map((_, i) => i)
      .filter((i) => enabled[i])
    if (enabledIndices.length === 0) {
      onClose()
      onNoOpResolve()
      return
    }

    const sendEmailEnabled = preparedActions.some(
      (pa, i) => pa.type === 'send_email' && enabled[i]
    )
    if (sendEmailEnabled) {
      // Trigger compose validation + onSend. react-hook-form runs the
      // resolver inside a Promise chain (even for sync zod schemas), so
      // we MUST await — reading composePayloadRef synchronously after
      // submit() races validation and always sees null.
      composePayloadRef.current = null
      await composeRef.current?.submit()
      if (!composePayloadRef.current) {
        // Compose validation failed (e.g. empty body, invalid recipient).
        // ComposePanel renders its own field errors; just stay open.
        return
      }
    }

    if (action.id == null) {
      // Legacy summaries pre-Phase-2 don't include the rec id; the new
      // endpoint is keyed on it. Old data won't apply via this flow —
      // we mark it done client-side and tell the user nothing was sent.
      notifyInfo(
        'This recommendation is from an older format and can’t be applied. Marking it as done.'
      )
      onClose()
      onNoOpResolve()
      return
    }

    // Build the action dicts the backend expects. Per-action shapes are
    // defined by ApplyRecommendationUsecase; we only send fields the
    // backend reads.
    const noteAttachments: File[] = []
    const emailAttachments: File[] = []
    const actionsForBackend: Array<Record<string, unknown>> = []

    for (const i of enabledIndices) {
      const pa = preparedActions[i]
      if (pa.type === 'update_stage') {
        const stageId = selectedStageId ?? pa.target_stage_id
        actionsForBackend.push({
          type: 'update_stage',
          target_stage_id: stageId,
        })
      } else if (pa.type === 'update_close_date') {
        if (!selectedCloseDate) continue
        actionsForBackend.push({
          type: 'update_close_date',
          target_close_date: selectedCloseDate,
        })
      } else if (pa.type === 'create_reminder') {
        if (!reminderTitle || !selectedReminderDate) continue
        actionsForBackend.push({
          type: 'create_reminder',
          reminder_title: reminderTitle,
          reminder_date: selectedReminderDate,
          deal_id: pa.deal_id,
          contact_company_id: pa.contact_company_id,
        })
      } else if (pa.type === 'reschedule_reminder') {
        if (!selectedRescheduleDate) continue
        actionsForBackend.push({
          type: 'reschedule_reminder',
          reminder_id: pa.reminder_id,
          target_date: selectedRescheduleDate,
        })
      } else if (pa.type === 'delete_reminder') {
        actionsForBackend.push({
          type: 'delete_reminder',
          reminder_id: pa.reminder_id,
        })
      } else if (pa.type === 'create_contact') {
        // Defense in depth: zod-driven Apply gate already blocks this
        // path when the form is invalid, but skip again here so a
        // bypass (e.g., toggle race) can't push a malformed payload.
        const formValues = getCreateContactValues()
        const cc = formValues.contact_company
        if (!cc || !createContactIsValid) {
          continue
        }
        const trimmedTitle = formValues.title?.trim() ?? ''
        const trimmedPhone = formValues.phone_number?.trim() ?? ''
        const basePayload: Record<string, unknown> = {
          type: 'create_contact',
          first_name: formValues.first_name.trim(),
          last_name: formValues.last_name.trim(),
          email: formValues.email.trim().toLowerCase(),
          // Empty strings → null so the backend treats them as "unset"
          // rather than blanking the column with a literal "" later.
          title: trimmedTitle ? trimmedTitle : null,
          phone_number: trimmedPhone ? trimmedPhone : null,
          extraction_confidence: pa.extraction_confidence,
        }
        if (cc.id > 0) {
          basePayload.contact_company_id = cc.id
        } else {
          // CreatableSelect's ``Create "X"`` path emits {id: -1, name}.
          // Apply handler creates the ContactCompany on submit; we just
          // forward the typed name. Schema's refine guarantees name is
          // non-empty when id <= 0.
          basePayload.new_company_name = cc.name?.trim() ?? ''
        }
        actionsForBackend.push(basePayload)
      } else if (pa.type === 'update_contact') {
        const formValues = getUpdateContactValues()
        // Only forward fields the LLM actually suggested — clearing an
        // unrelated field shouldn't blank a stored value (design
        // point #9). For each suggested field, send the user's edited
        // value if non-empty, else null (skip applying that field).
        const titleTarget =
          pa.target_title != null ? formValues.title?.trim() || null : null
        const phoneTarget =
          pa.target_phone_number != null
            ? formValues.phone_number?.trim() || null
            : null
        // Bail out client-side when both fields are empty — no-op
        // apply, backend would refuse with 400 anyway, this avoids
        // the round-trip.
        if (titleTarget == null && phoneTarget == null) continue
        actionsForBackend.push({
          type: 'update_contact',
          contact_id: pa.contact_id,
          original_modified_at: pa.original_modified_at,
          target_title: titleTarget,
          target_phone_number: phoneTarget,
          extraction_confidence: pa.extraction_confidence,
        })
      } else if (pa.type === 'create_deal') {
        if (!createDealProductId) continue
        actionsForBackend.push({
          type: 'create_deal',
          contact_company_id: pa.contact_company_id,
          contact_id: pa.contact_id,
          trigger_email_id: pa.trigger_email_id,
          target_product_id: createDealProductId,
          target_deal_name: createDealName.trim() || null,
          target_close_date: createDealCloseDate || null,
          target_monthly_volume: createDealVolume
            ? Number(createDealVolume)
            : null,
          target_annual_revenue: createDealRevenue
            ? Number(createDealRevenue)
            : null,
        })
      } else if (pa.type === 'add_note') {
        const values = getValues()
        actionsForBackend.push({
          type: 'add_note',
          draft_content: values.note,
        })
        noteAttachments.push(...values.attachments)
      } else if (pa.type === 'send_email') {
        const payload = composePayloadRef.current
        if (!payload) continue
        // Extract compose form values from the FormData ComposePanel
        // produced. ``to``/``cc``/``bcc`` arrive as JSON-stringified
        // recipient lists; we forward them as parsed arrays so the
        // backend doesn't have to double-decode.
        const fd = payload.formData
        const parseList = (key: string) => {
          const raw = fd.get(key)
          if (typeof raw !== 'string' || !raw) return []
          try {
            const parsed = JSON.parse(raw)
            return Array.isArray(parsed) ? parsed : []
          } catch {
            return []
          }
        }
        actionsForBackend.push({
          type: 'send_email',
          to: parseList('to'),
          cc: parseList('cc'),
          bcc: parseList('bcc'),
          subject: String(fd.get('subject') ?? ''),
          body: String(fd.get('body') ?? ''),
          // ComposePanel only sets reply_to_message_id when mode is
          // reply/reply_all; for embedded compose we use mode="compose"
          // and stitch the rec's reply_to_message_id from the prepared
          // action onto the FormData here. Already a Nylas UUID string
          // from the backend.
          reply_to_message_id: pa.reply_to_message_id ?? '',
        })
        for (const value of fd.getAll('attachments')) {
          if (value instanceof File) emailAttachments.push(value)
        }
      }
    }

    if (actionsForBackend.length === 0) {
      onClose()
      onNoOpResolve()
      return
    }

    const formData = new FormData()
    formData.append('actions', JSON.stringify(actionsForBackend))
    for (const file of emailAttachments) {
      formData.append('email_attachments', file)
    }
    for (const file of noteAttachments) {
      formData.append('note_attachments', file)
    }

    // Close the modal immediately and hand the request off to the card.
    // The card disables itself while the apply is in flight, then shows
    // a success / failure toast and triggers the summary refetch.
    onClose()
    onApply(formData, actionsForBackend.length)
  }

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClose()
  }

  const actionTitle = (pa: PreparedAction): string => {
    if (pa.type === 'update_stage') return 'Update deal stage'
    if (pa.type === 'update_close_date') return 'Update close date'
    if (pa.type === 'create_reminder') return 'Set follow-up reminder'
    if (pa.type === 'reschedule_reminder') return 'Reschedule reminder'
    if (pa.type === 'delete_reminder') return 'Delete reminder'
    if (pa.type === 'add_note') {
      // The note lands on whichever entity the card title links to —
      // company for ``unreplied_email`` / ``schedule_follow_up``, deal
      // otherwise, ``auto`` defers to whether ``deal_id`` is present.
      // Backend mirrors this exact rule in
      // ``apply_recommendation.py::_resolve_note_target``; keep both in
      // lockstep when adding new signal types.
      const linkTarget = SIGNAL_CONFIG[action.signal_type]?.linkTarget ?? 'auto'
      const targetIsCompany =
        linkTarget === 'company' || (linkTarget === 'auto' && !action.deal_id)
      return targetIsCompany ? 'Add company note' : 'Add deal note'
    }
    if (pa.type === 'send_email') return 'Send email'
    if (pa.type === 'create_contact') return 'Create contact'
    if (pa.type === 'update_contact') return 'Update contact'
    if (pa.type === 'create_deal') return 'Create deal'
    return (pa as PreparedAction).type
  }

  const actionDetail = (pa: PreparedAction, index: number): React.ReactNode => {
    // Special-case: send_email is force-unchecked when there's no email
    // integration. We still want to surface the CTA so the user knows
    // why the row is disabled and can click through to connect one.
    // This intentionally short-circuits the !enabled[index] guard below.
    if (pa.type === 'send_email' && !hasEmailIntegration) {
      return (
        <div
          className="mt-2 rounded-md border border-warning-border bg-warning-bg px-3 py-2 flex items-center justify-between gap-3"
          onClick={(e) => e.stopPropagation()}
        >
          <span className="text-sm text-warning">
            Connect an email integration to send replies from the recommendation
            card.
          </span>
          <Link
            to="/settings/email-integration/connections"
            className="text-sm font-medium text-warning underline shrink-0"
            onClick={() => onClose()}
          >
            Integrate Email
          </Link>
        </div>
      )
    }
    if (!enabled[index]) return null
    if (pa.type === 'update_stage') {
      return (
        <StageColorProvider value="pipeline">
          <div className="flex items-center gap-2 mt-1.5">
            <StagePill stageId={pa.current_stage_id} />
            <ArrowRight size={14} className="text-fg-muted flex-shrink-0" />
            <ReactSelectWrapper
              className="min-w-[160px]"
              height="38px"
              options={pipelineStageOptions}
              value={selectedStageOption}
              onChange={(option: unknown) => {
                const typed = option as SingleValue<{
                  label: string
                  value: number
                }>
                if (typed) setSelectedStageId(typed.value)
              }}
              formatOptionLabel={(data: unknown) => {
                const opt = data as {
                  label: string
                  bg?: string
                  text?: string
                }
                return (
                  <TextPill
                    backgroundColor={opt.bg ?? DEFAULT_STAGE_COLOR}
                    textColor={opt.text ?? colors['fg']}
                  >
                    {opt.label}
                  </TextPill>
                )
              }}
              isSearchable={false}
              menuPortalTarget={document.body}
            />
          </div>
        </StageColorProvider>
      )
    }
    if (pa.type === 'update_close_date') {
      return (
        <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
          <DatePicker
            value={selectedCloseDate}
            onChange={(val) => setSelectedCloseDate(val)}
          />
        </div>
      )
    }
    if (pa.type === 'create_reminder') {
      return (
        <div className="mt-1.5 space-y-2" onClick={(e) => e.stopPropagation()}>
          <input
            type="text"
            value={reminderTitle}
            onChange={(e) =>
              // Clamp on change so a paste >50 chars can't bypass the
              // ``maxLength`` HTML attr (which only blocks keyboard
              // entry, not paste). Paired with the zod check below.
              setReminderTitle(
                e.target.value.slice(0, REMINDER_TITLE_MAX_LENGTH)
              )
            }
            maxLength={REMINDER_TITLE_MAX_LENGTH}
            placeholder="Reminder title"
            className="w-full rounded-md border border-line px-3 py-1.5 text-sm text-fg placeholder:text-fg-muted focus:border-brand focus:outline-none"
          />
          <DatePicker
            value={selectedReminderDate}
            onChange={(val) => setSelectedReminderDate(val)}
          />
          {createReminderError && enabled[index] && (
            <p className="text-xs text-error">{createReminderError}</p>
          )}
        </div>
      )
    }
    if (pa.type === 'reschedule_reminder') {
      return (
        <div className="mt-1.5" onClick={(e) => e.stopPropagation()}>
          <DatePicker
            value={selectedRescheduleDate}
            onChange={(val) => setSelectedRescheduleDate(val)}
          />
          {rescheduleReminderError && enabled[index] && (
            <p className="mt-1 text-xs text-error">{rescheduleReminderError}</p>
          )}
        </div>
      )
    }
    if (pa.type === 'create_contact') {
      // Editable form for the suggested contact. The LLM extraction
      // pre-fills first/last name + email + (sometimes) title/phone,
      // but the user can correct any of them before applying. Validation
      // (zod) enforces required name + valid email + picked company —
      // mirrors the manual contact-create form's strictness via shared
      // validator atoms. Apply gates on ``createContactIsValid`` so a
      // bad email can't reach the backend.
      // Inputs match the sider's ``text-base`` scale (other content
      // here — action title, recommendation body, badges — uses
      // text-base too, per the "single sider scale" rule). py-1.5
      // produces a ~38px tall input (border 1px + 6px + 24px line
      // height + 6px + border 1px) so the input row aligns vertically
      // with the CompanyAutocomplete below it (height="38px"). Errors
      // stay text-xs since they're secondary feedback.
      const inputClass =
        'w-full rounded-md border border-line px-3 py-1.5 text-base text-fg placeholder:text-fg-muted focus:border-brand focus:outline-none'
      const labelClass = 'block text-sm text-fg-muted mb-0.5'
      const errorClass = 'mt-0.5 text-xs text-error'
      return (
        <div
          className="mt-2 space-y-2 rounded-md border border-line bg-surface p-3"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex gap-2">
            <div className="flex-1">
              <label className={labelClass}>First name</label>
              <input
                type="text"
                {...registerCreateContact('first_name')}
                placeholder="First name"
                className={inputClass}
              />
              {createContactErrors.first_name && (
                <p className={errorClass}>
                  {createContactErrors.first_name.message}
                </p>
              )}
            </div>
            <div className="flex-1">
              <label className={labelClass}>Last name</label>
              <input
                type="text"
                {...registerCreateContact('last_name')}
                placeholder="Last name"
                className={inputClass}
              />
              {createContactErrors.last_name && (
                <p className={errorClass}>
                  {createContactErrors.last_name.message}
                </p>
              )}
            </div>
          </div>
          <div>
            <label className={labelClass}>Email</label>
            <input
              type="email"
              {...registerCreateContact('email')}
              placeholder="name@company.com"
              className={inputClass}
            />
            {createContactErrors.email && (
              <p className={errorClass}>{createContactErrors.email.message}</p>
            )}
          </div>
          <div>
            <CompanyAutocomplete
              control={createContactControl}
              name="contact_company"
              label="Company"
              placeholder="Search for an existing company..."
              menuPosition="fixed"
              height="38px"
              noOptionsMessage={({ inputValue }) =>
                inputValue ? 'No companies found' : 'Type to search…'
              }
            />
            {/* CreatableSelect emits {id: -1, name} when the user picks
                ``Create "X"``. Surface a clear hint so the user knows
                the apply will create a new ContactCompany rather than
                attaching the contact to an existing row. */}
            {selectedContactCompany?.id === -1 &&
              (selectedContactCompany.name?.trim().length ?? 0) > 0 && (
                <p className="mt-0.5 text-xs text-fg-muted">
                  A new company &ldquo;{selectedContactCompany.name?.trim()}
                  &rdquo; will be created when you apply.
                </p>
              )}
            {/* Picker renders its own ErrorLabel via CreatableSelect's
                fieldState.error wiring — no manual error block needed. */}
          </div>
          <div>
            <label className={labelClass}>Title</label>
            <input
              type="text"
              {...registerCreateContact('title')}
              placeholder="Optional title"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Phone</label>
            {/* Mirrors the manual contact form: country dropdown +
                E.164-formatted input. Validation, error rendering,
                and country-prefix handling are all encapsulated by
                PhoneInput, which surfaces its own ErrorLabel from
                fieldState.error. */}
            <PhoneInput
              control={createContactControl}
              name="phone_number"
              optional
            />
          </div>
        </div>
      )
    }
    if (pa.type === 'update_contact') {
      // Per-field "current → editable new" rows, matching the
      // update_stage layout (StagePill → ArrowRight → editable
      // dropdown). Only show fields the LLM actually proposed a
      // change for; fields with target_* === null aren't rendered
      // at all. The right-side input is prefilled with the LLM's
      // target value; the user can edit before applying or clear
      // to skip applying that field. Validation (zod) only kicks
      // in for phone (E.164); title is free-text.
      const titleHasSuggestion = pa.target_title != null
      const phoneHasSuggestion = pa.target_phone_number != null
      // "Recently edited" hint when the captured-modified timestamp is
      // within the last hour: somebody is actively touching this contact
      // and the LLM extraction may already be stale before apply.
      const modifiedRecently = (() => {
        const t = Date.parse(pa.original_modified_at)
        if (Number.isNaN(t)) return false
        return Date.now() - t < 60 * 60 * 1000
      })()
      const inputClass =
        'flex-1 min-w-0 rounded-md border border-line px-3 py-1.5 text-base text-fg placeholder:text-fg-muted focus:border-brand focus:outline-none'
      const labelClass = 'block text-sm text-fg-muted mb-0.5'
      const currentClass = 'text-fg-muted text-base whitespace-nowrap'
      return (
        <div
          className="mt-2 space-y-2 rounded-md border border-line bg-surface p-3"
          onClick={(e) => e.stopPropagation()}
        >
          {modifiedRecently && (
            <div className="rounded-md border bg-yellow-50 border-b border-yellow-300 text-yellow-800 px-2 py-1.5 text-xs">
              Contact recently edited — verify before applying.
            </div>
          )}
          {titleHasSuggestion && (
            <div>
              <label className={labelClass}>Title</label>
              <div className="flex items-center gap-2">
                <span className={currentClass}>
                  {pa.current_title || 'No Value'}
                </span>
                <ArrowRight size={14} className="text-fg-muted flex-shrink-0" />
                <input
                  type="text"
                  {...registerUpdateContact('title')}
                  placeholder="Clear to skip"
                  className={inputClass}
                />
              </div>
            </div>
          )}
          {phoneHasSuggestion && (
            <div>
              <label className={labelClass}>Phone</label>
              <div className="flex items-center gap-2">
                <span className={currentClass}>
                  {pa.current_phone_number || 'No Value'}
                </span>
                <ArrowRight size={14} className="text-fg-muted flex-shrink-0" />
                {/* Same E.164-formatted phone input as create_contact —
                    PhoneInput surfaces its own ErrorLabel from
                    fieldState.error when validation fails. */}
                <div className="flex-1 min-w-0">
                  <PhoneInput
                    control={updateContactControl}
                    name="phone_number"
                    optional
                  />
                </div>
              </div>
              {updateContactErrors.phone_number && (
                <p className="mt-0.5 text-xs text-error">
                  {updateContactErrors.phone_number.message}
                </p>
              )}
            </div>
          )}
        </div>
      )
    }
    if (pa.type === 'create_deal') {
      return (
        <div
          className="mt-2 space-y-2 rounded-md border border-line bg-surface-alt p-3"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-sm">
            <span className="font-medium text-fg">{action.company_name}</span>
            {action.contact_name && (
              <>
                <span className="text-fg-muted"> · </span>
                <span className="text-fg-muted">{action.contact_name}</span>
              </>
            )}
          </div>
          <div>
            <label className="block text-xs text-fg-muted mb-0.5">
              Product <span className="text-error">*</span>
            </label>
            <ProductSelector
              value={createDealProductId}
              onChange={setCreateDealProductId}
            />
          </div>
          <div>
            <label className="block text-xs text-fg-muted mb-0.5">
              Deal name
            </label>
            <input
              type="text"
              value={createDealName}
              onChange={(e) => setCreateDealName(e.target.value)}
              placeholder="Optional deal name"
              className="w-full rounded-md border border-line px-3 py-1.5 text-sm text-fg placeholder:text-fg-muted focus:border-brand focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-fg-muted mb-0.5">
              Close date
            </label>
            <DatePicker
              value={createDealCloseDate}
              onChange={(val) => setCreateDealCloseDate(val)}
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-fg-muted mb-0.5">
                Monthly volume (cases)
              </label>
              <input
                type="number"
                min={0}
                value={createDealVolume}
                onChange={(e) => setCreateDealVolume(e.target.value)}
                placeholder="—"
                className="w-full rounded-md border border-line px-3 py-1.5 text-sm text-fg placeholder:text-fg-muted focus:border-brand focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs text-fg-muted mb-0.5">
                Annual revenue ($)
              </label>
              <input
                type="number"
                min={0}
                value={createDealRevenue}
                onChange={(e) => setCreateDealRevenue(e.target.value)}
                placeholder="—"
                className="w-full rounded-md border border-line px-3 py-1.5 text-sm text-fg placeholder:text-fg-muted focus:border-brand focus:outline-none"
              />
            </div>
          </div>
          {createDealMissingProduct && (
            <p className="text-xs text-warning">
              Pick a product to enable Apply.
            </p>
          )}
        </div>
      )
    }
    if (pa.type === 'add_note') {
      return (
        <div className="mt-2" onClick={(e) => e.stopPropagation()}>
          <NoteManager
            control={control}
            noteName="note"
            attachmentsName="attachments"
            placeholder="Edit note before applying..."
            enableMentions
            editorClassName="h-28 bg-surface-alt"
            toolbarClassName="bg-surface-alt"
            label=""
          />
        </div>
      )
    }
    if (pa.type === 'send_email') {
      // The no-integration CTA path is handled by the early-return above
      // (which short-circuits the !enabled[index] guard); reaching here
      // means the user has at least one integration.
      // Subject-line affordance: replies expose a "See thread" link
      // (opens the trigger inbound via EmailsModal), fresh outbound
      // sends show a "New thread" badge. Both render inline with the
      // subject input via ComposePanel's ``subjectPrefix`` slot so
      // ComposePanel keeps its layout authority.
      // Same ComposePanel as the inbox standalone compose, `embedded`
      // only hides Send/Cancel since the modal's global Apply triggers
      // send. The h-[300px] is the compact default (was 480) so the
      // compose doesn't dominate the actions list and the user can see
      // adjacent reminder / note actions without scrolling. The body
      // editor still scrolls internally if a draft is long.
      // Subject-prefix font is matched to the subject input's ``text-sm``
      // so both sit on the same baseline inside the row's flex
      // ``items-center`` alignment. ``leading-5`` mirrors the input's
      // line height to lock the visual baseline.
      const subjectPrefix =
        pa.trigger_email_id != null ? (
          <button
            type="button"
            onClick={() => setThreadEmailId(pa.trigger_email_id)}
            className="text-sm leading-5 text-fb-dark-green underline font-medium whitespace-nowrap"
          >
            See thread
          </button>
        ) : (
          <span className="inline-flex items-center gap-1 rounded border border-line bg-surface-alt px-2 py-0.5 text-sm leading-5 font-medium text-fg-secondary whitespace-nowrap">
            New thread
          </span>
        )
      return (
        <div
          className="mt-2 h-[300px] flex flex-col rounded-md border border-line overflow-hidden bg-surface"
          onClick={(e) => e.stopPropagation()}
        >
          <ComposePanel
            ref={composeRef}
            mode="compose"
            standalone
            embedded
            compactSignature
            integrationEmail={user?.email}
            initialTo={[pa.to_email]}
            initialSubject={pa.subject}
            initialBody={plainTextToHtml(pa.draft_body)}
            initialContacts={[{ email: pa.to_email, name: pa.to_name ?? '' }]}
            subjectPrefix={subjectPrefix}
            onSend={(payload) => {
              composePayloadRef.current = payload
            }}
            onDiscard={() => undefined}
          />
        </div>
      )
    }
    return null
  }

  // Rec-context section data: same icon vocabulary the card uses.
  const ownerAgent = recommendationAgents.find((agent) =>
    agent.signal_types.includes(action.signal_type)
  )
  const signalTypeLabel = ownerAgent?.singular_label ?? ''
  const SignalTypeIcon = resolveAgentIcon(ownerAgent?.icon ?? '')
  const { iconBg, iconColor } = URGENCY_CIRCLE_STYLES[action.urgency]
  const UrgencyIcon = URGENCY_ICONS[action.urgency]
  const urgencyLabel = URGENCY_TOOLTIPS[action.urgency].title

  return (
    <RightPanel
      onClose={onClose}
      header={
        <>
          <h3 className="text-xl font-semibold text-fg leading-tight truncate min-w-0">
            {action.action}
          </h3>
          <span className="inline-flex items-center gap-1 rounded border border-line bg-surface-alt px-2 py-0.5 text-base font-medium leading-tight text-fg-secondary flex-shrink-0">
            <SignalTypeIcon size={16} />
            {signalTypeLabel}
          </span>
          <span
            className={cn(
              'inline-flex items-center gap-1 rounded px-2 py-0.5 text-base font-medium leading-tight border border-transparent flex-shrink-0',
              iconBg,
              iconColor
            )}
          >
            <UrgencyIcon size={16} />
            {urgencyLabel}
          </span>
        </>
      }
      footer={
        <>
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 text-base rounded border border-line text-fg-secondary hover:bg-surface-alt transition-colors"
          >
            Cancel
          </button>
          {createDealMissingProduct ? (
            <Tooltip
              title="Product required"
              content="Pick a product to enable Apply."
              placement="top"
              delay={[200, 0]}
            >
              <button
                type="button"
                onClick={handleConfirm}
                disabled
                className="inline-flex items-center gap-1.5 px-4 py-2 text-base rounded bg-fb-dark-green text-white hover:bg-fb-dark-green-hover transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {enabledCount > 0 ? (
                  <>
                    <Play size={16} fill="currentColor" />
                    Apply Actions ({enabledCount})
                  </>
                ) : (
                  'Resolve (No Action)'
                )}
              </button>
            </Tooltip>
          ) : (
            <button
              type="button"
              onClick={handleConfirm}
              disabled={hasReminderError || createContactInvalid}
              title={
                createContactInvalid
                  ? 'Fill in name, email, and company for the new contact before applying.'
                  : undefined
              }
              className="inline-flex items-center gap-1.5 px-4 py-2 text-base rounded bg-fb-dark-green text-white hover:bg-fb-dark-green-hover transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {enabledCount > 0 ? (
                <>
                  <Play size={16} fill="currentColor" />
                  Apply Actions ({enabledCount})
                </>
              ) : (
                'Resolve (No Action)'
              )}
            </button>
          )}
        </>
      }
      subHeader={
        // Always-visible context above the scrolling body. Lives outside
        // the scroll container, so action-grid descendants (compose
        // panel, date picker, react-select) can't bleed through —
        // structural isolation rather than z-index gymnastics.
        <>
          <EntitySummary action={action} />
          <p className="text-base text-fg mt-3">{action.recommendation}</p>
          {/* Tabs: Actions (the prepared-actions list, default) and
              Activity (entity timeline). The Activity tab is mounted
              only when selected so its event-history query fires
              lazily. The tab is hidden entirely for recs with no
              entity binding (today: contact_suggestion ``create`` —
              the contact + company don't exist yet, so there's no
              timeline to show). Label follows the signal's
              ``linkTarget`` so company-scoped recs (e.g.
              unreplied_email, schedule_follow_up) say "Company
              Activity" even when a deal_id happens to be populated;
              deal-scoped recs say "Deal Activity". ``auto`` recs
              fall back to deal_id presence. */}
          {(() => {
            const hasActivityEntity =
              !!action.deal_id || !!action.contact_company_id
            const linkTarget = SIGNAL_CONFIG[action.signal_type]?.linkTarget
            const activityLabel =
              linkTarget === 'company'
                ? 'Company Activity'
                : linkTarget === 'deal'
                  ? 'Deal Activity'
                  : action.deal_id
                    ? 'Deal Activity'
                    : 'Company Activity'
            const tabs: {
              key: PanelTab
              title: string | React.JSX.Element
            }[] = [
              {
                key: 'quick-actions',
                title: (
                  <span className="inline-flex items-center gap-1.5">
                    <Play size={16} fill="currentColor" />
                    Actions
                  </span>
                ),
              },
            ]
            if (hasActivityEntity) {
              tabs.push({ key: 'activity', title: activityLabel })
            }
            return (
              <div className="mt-12">
                <Tabs<PanelTab>
                  tabs={tabs}
                  // FbUI defaults: 14px, inactive weight 300 / active 500.
                  // Tabs sit BELOW the sider's 16px body scale so they read
                  // as control chrome (section toggle), not as headers
                  // competing with action titles. Underline still carries
                  // the active state.
                  selectedTab={activeTab}
                  setSelectedTab={setActiveTab}
                />
              </div>
            )
          })()}
        </>
      }
    >
      {activeTab === 'quick-actions' && (
        <div className="grid grid-cols-[16px_1fr] gap-x-3 mt-4">
          {preparedActions.map((pa, index) => {
            const sendEmailNoIntegration =
              pa.type === 'send_email' && !hasEmailIntegration
            return (
              <React.Fragment key={`${pa.type}-${index}`}>
                {/* Checkbox column. ``h-6`` matches the title row's
                    fixed line height so the checkbox stays aligned to
                    the title's vertical center, regardless of how tall
                    the reason + editor rows below it grow. */}
                <div
                  className={cn(
                    'flex justify-center items-center h-6',
                    !enabled[index] && 'opacity-60'
                  )}
                >
                  <Checkbox
                    checked={enabled[index]}
                    disabled={sendEmailNoIntegration}
                    onChange={() => toggleAction(index)}
                  />
                </div>
                {/* Content. Single sider scale — everything ``text-base``
                    (16px) with weight + color doing the hierarchy:
                    title is semibold + fg, reason is normal + fg-
                    secondary. Avoids the earlier 18/18 size contrast
                    that read as two equal-weight lines, and the 14px
                    fg-muted that read as barely-legible noise. */}
                <div className={cn('min-w-0', !enabled[index] && 'opacity-60')}>
                  <span className="text-base font-semibold text-fg leading-6 block h-6">
                    {actionTitle(pa)}
                  </span>
                  {pa.reason && (
                    <p className="text-base text-fg-secondary mt-1 leading-snug">
                      {pa.reason}
                    </p>
                  )}
                  {actionDetail(pa, index)}
                </div>
                {/* Connector line — doubled vertical padding so adjacent
                  actions breathe; the connector itself stays a hairline. */}
                {index < preparedActions.length - 1 && (
                  <>
                    <div className="flex justify-center py-2">
                      <div className="w-px h-full bg-line" />
                    </div>
                    <div />
                  </>
                )}
              </React.Fragment>
            )
          })}
        </div>
      )}

      {activeTab === 'activity' && (
        <RecommendationActivityTab action={action} />
      )}
      {threadEmail && (
        <EmailsModal
          show={!!threadEmail}
          onHide={() => setThreadEmailId(null)}
          emailOnlyMode
          initialEmail={threadEmail}
        />
      )}
    </RightPanel>
  )
}

// ---------------------------------------------------------------------------
// Card Skeleton — shared between the initial summary-loading placeholder
// (rendered by RecommendationsCard) and the per-card "applying" state.
// Sized to match a real EnrichedActionCard so the column doesn't visually
// shift between states.
// ---------------------------------------------------------------------------

export const EnrichedActionCardSkeleton: React.FC = () => (
  <div className="rounded-lg border border-line bg-surface p-4 space-y-3 animate-pulse">
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-full bg-surface-disabled flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-2/5 rounded bg-surface-disabled" />
        <div className="h-3 w-3/5 rounded bg-surface-disabled" />
      </div>
    </div>
    <div className="space-y-2 pl-12">
      <div className="h-3 w-full rounded bg-surface-disabled" />
      <div className="h-3 w-11/12 rounded bg-surface-disabled" />
      <div className="h-3 w-4/5 rounded bg-surface-disabled" />
    </div>
    <div className="flex items-center justify-between pt-2 border-t border-line-subtle">
      <div className="h-3 w-20 rounded bg-surface-disabled" />
      <div className="flex gap-2">
        <div className="h-6 w-16 rounded bg-surface-disabled" />
        <div className="h-6 w-16 rounded bg-surface-disabled" />
      </div>
    </div>
  </div>
)

// ---------------------------------------------------------------------------
// Resolved Badge
// ---------------------------------------------------------------------------

const GREEN_BADGE = 'bg-fb-green-50 text-fb-dark-green border-fb-green-100'
const GREY_BADGE = 'bg-surface-alt text-fg-secondary border-line-subtle'

function getResolvedBadge(
  status: ActionClickStatus | null,
  autoCompleted: boolean,
  resolutionStatus?: ResolutionStatus | null
): { label: string; className: string } | null {
  // Prefer enriched_action-level resolution_status (set by LLM or user action)
  if (resolutionStatus) {
    switch (resolutionStatus) {
      case 'auto_resolved':
        return { label: 'Auto-resolved', className: GREEN_BADGE }
      case 'auto_dismissed':
        return { label: 'Auto-dismissed', className: GREY_BADGE }
      case 'user_resolved':
        return { label: 'Applied', className: GREEN_BADGE }
      case 'user_dismissed':
        return { label: 'Dismissed', className: GREY_BADGE }
    }
  }
  // Fallback: existing click-based logic for backward compat with old data
  if (status === 'done') {
    return {
      label: autoCompleted ? 'Auto-resolved' : 'Applied',
      className: GREEN_BADGE,
    }
  }
  if (status === 'dismissed') {
    return {
      label: autoCompleted ? 'Auto-resolved' : 'Dismissed',
      className: autoCompleted ? GREEN_BADGE : GREY_BADGE,
    }
  }
  return null
}

// ---------------------------------------------------------------------------
// EnrichedActionCard
// ---------------------------------------------------------------------------

/**
 * Compact action card with risk indicator and company link.
 *
 * Features:
 * - Colored icon circle driven by urgency (red=do_now, amber=today, green=this_week)
 * - Black border, no left stripe
 * - Dismiss (7-day suppression) and Apply (multi-action dialog) in footer
 * - Link to deal (email_stage_change) or company (all others)
 * - Context snippets from last interaction
 */
export const EnrichedActionCard: React.FC<EnrichedActionCardProps> = ({
  action,
  onCardClick,
  onStatusClick,
  actionStatus = null,
  actionAutoCompleted = false,
  onApplied,
  onNoOpResolve,
  readOnly = false,
  isRefreshing = false,
  onApplyClick,
  recommendationAgents = [],
  dimWhenResolved = true,
}) => {
  const { iconBg, iconColor } = URGENCY_CIRCLE_STYLES[action.urgency]
  const UrgencyIcon = URGENCY_ICONS[action.urgency]
  const urgencyLabel = URGENCY_TOOLTIPS[action.urgency].title
  // Chip label + icon = the singular form + icon of the agent that owns
  // this rec's signal_type. Single source of truth (BE AGENT_REGISTRY);
  // changing an agent's name or icon updates section header AND card
  // badge together.
  const ownerAgent = recommendationAgents.find((agent) =>
    agent.signal_types.includes(action.signal_type)
  )
  const signalTypeLabel = ownerAgent?.singular_label ?? ''
  const SignalTypeIcon = resolveAgentIcon(ownerAgent?.icon ?? '')

  const isStatusFinalized =
    actionStatus === 'done' || actionStatus === 'dismissed'
  const resolvedBadge = getResolvedBadge(
    actionStatus,
    actionAutoCompleted,
    action.resolution_status
  )

  const preparedActions = useMemo(
    () => action.prepared_actions ?? [],
    [action.prepared_actions]
  )
  const allActionsStale = preparedActions.every((pa) => pa.stale)
  const applyDisabled =
    readOnly || isRefreshing || isStatusFinalized || allActionsStale

  const applyDisabledReason:
    | 'readonly'
    | 'refreshing'
    | 'finalized'
    | 'stale'
    | undefined = readOnly
    ? 'readonly'
    : isRefreshing
      ? 'refreshing'
      : isStatusFinalized
        ? 'finalized'
        : allActionsStale
          ? 'stale'
          : undefined

  // Whole-card click opens the apply panel — same gating as the Apply
  // button. Buttons and entity links inside the card already
  // ``stopPropagation`` so they don't double-trigger this handler.
  const cardClickable =
    !!onApplyClick && preparedActions.length > 0 && !applyDisabled

  return (
    <div
      onClick={
        cardClickable
          ? () => {
              onCardClick?.()
              onApplyClick?.(action)
            }
          : undefined
      }
      className={cn(
        'rounded-lg border border-fg bg-surface p-3',
        cardClickable
          ? 'cursor-pointer hover:bg-surface-alt transition-colors'
          : 'cursor-default',
        dimWhenResolved && actionStatus === 'done' && 'opacity-50',
        dimWhenResolved && actionStatus === 'dismissed' && 'opacity-40'
      )}
    >
      {/* Header: short summary (action) on the left, badge cluster on
          the right. The old urgency circle that lived to the left of
          the title is gone — its information is now carried by the
          colored urgency badge in the cluster below, which lets the
          card text reclaim the full width of the row. */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-fg text-lg leading-tight">
            {action.action}
          </h4>
        </div>

        {/* Badge cluster: type + (optional) resolved state + priority.
            Priority sits at the rightmost edge so it lands first in the
            user's eye scanning from outside the card inward. ``flex-wrap``
            lets badges drop to a second line on narrow cards instead of
            squeezing the title. */}
        <div className="flex-shrink-0 flex flex-wrap items-center justify-end gap-1.5">
          <span className="inline-flex items-center gap-1 rounded border border-line bg-surface-alt px-2 py-0.5 text-base font-medium leading-tight text-fg-secondary">
            <SignalTypeIcon size={16} />
            {signalTypeLabel}
          </span>

          <Tooltip
            title={URGENCY_TOOLTIPS[action.urgency].title}
            content={URGENCY_TOOLTIPS[action.urgency].content}
            placement="top"
            delay={[500, 0]}
          >
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded px-2 py-0.5 text-base font-medium leading-tight border border-transparent',
                iconBg,
                iconColor
              )}
            >
              <UrgencyIcon size={16} />
              {urgencyLabel}
            </span>
          </Tooltip>
        </div>
      </div>

      {/* Auto-resolution reason text. The matching badge has moved into
          the footer (where action buttons sit on un-resolved cards) so
          resolved cards visually swap "buttons" for "status" in the
          same spot. User-resolved/dismissed paths have no reason text
          and so render nothing here. */}
      {action.resolution_reason &&
        action.resolution_status?.startsWith('auto_') && (
          <p className="text-base text-fg-muted italic mt-1">
            {action.resolution_reason}
          </p>
        )}

      {/* Long recommendation text. Short summary now lives in the
          header above. While the LLM is re-evaluating (isRefreshing),
          swap this paragraph for a small in-place skeleton so the card
          visibly identifies which rec is busy without losing its frame
          (header, footer all stay put). */}
      {isRefreshing ? (
        <div className="mt-2 space-y-1.5 animate-pulse">
          <div className="h-5 bg-surface-alt rounded w-full" />
          <div className="h-5 bg-surface-alt rounded w-5/6" />
          <div className="h-5 bg-surface-alt rounded w-2/3" />
        </div>
      ) : (
        <p className="text-lg text-fg-secondary mt-2">
          {action.recommendation}
        </p>
      )}

      {/* Action footer: entity summary (left, with deal/company
              links) + Apply + Dismiss (right). The entity summary
              replaces the older "View deal" / "View company" button —
              its company_name and deal_name are now the navigation
              affordance. */}
      <div className="flex items-center justify-between gap-3 mt-2 pt-2 border-t border-line-subtle">
        <EntitySummary action={action} onCardClick={onCardClick} />
        <div className="flex items-center gap-1 flex-shrink-0">
          {/* Resolved cards display the status badge in place of the
              Apply / Dismiss buttons — the row's right edge always
              answers "what's the next action?" (or, here, "this is
              already done"). */}
          {isStatusFinalized && resolvedBadge && (
            <span
              className={cn(
                'inline-flex items-center rounded border px-2 py-0.5 text-base font-medium leading-tight',
                resolvedBadge.className
              )}
            >
              {resolvedBadge.label}
            </span>
          )}
          {onApplied &&
            onNoOpResolve &&
            preparedActions.length > 0 &&
            !isStatusFinalized && (
              <>
                {applyDisabled ? (
                  <Tooltip
                    title={
                      DISABLED_TOOLTIP[applyDisabledReason ?? 'stale'].title
                    }
                    content={
                      DISABLED_TOOLTIP[applyDisabledReason ?? 'stale'].content
                    }
                    placement="top"
                    delay={[200, 0]}
                  >
                    <button
                      type="button"
                      disabled
                      className="inline-flex items-center gap-1 px-2 py-1 text-base rounded opacity-40 cursor-not-allowed bg-fb-dark-green text-white"
                    >
                      <Play size={16} fill="currentColor" />
                      <span>Apply</span>
                    </button>
                  </Tooltip>
                ) : (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation()
                      onApplyClick?.(action)
                    }}
                    className="inline-flex items-center gap-1 px-2 py-1 text-base rounded transition-colors bg-fb-dark-green text-white hover:bg-fb-dark-green-hover font-medium"
                  >
                    <Play size={16} fill="currentColor" />
                    <span>Apply</span>
                  </button>
                )}
              </>
            )}
          {!isStatusFinalized && (
            <Tooltip
              title="Dismiss"
              content={
                readOnly
                  ? 'You can only dismiss your own recommendations.'
                  : 'Hide this recommendation for 7 days.'
              }
              placement="top"
              delay={[300, 0]}
            >
              <button
                type="button"
                disabled={readOnly || isRefreshing}
                onClick={(e) => {
                  e.stopPropagation()
                  onStatusClick?.('dismissed')
                }}
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-1 text-base rounded transition-colors',
                  readOnly || isRefreshing
                    ? 'opacity-40 cursor-not-allowed text-fg-secondary'
                    : 'text-fg-secondary hover:text-fg hover:bg-surface-alt'
                )}
                aria-label="Dismiss"
              >
                <X size={18} />
                <span>Dismiss</span>
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  )
}
